import React from 'react';

import { ErrorResponse as RoMaxPriceErrorResponse } from '~/Api/User/ProductGroup/RoMaxPrice';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage, removeFlashMessageByText } from '~/FlashMessage';
import { commitRoMaxPriceUpdate, createExport, ExportType, handleRoMaxPriceUpdateResponse } from '~/RoMaxPrice';
import { formatCountriesError } from '~/UI';
import { Export, UserProductGroup } from '~/Api/User/ProductGroup';

import Button from '~/UI/Button';
import primaryIconButtonStyles from '~/UI/Button/primary-icon-full.less';
import Icon from '~/UI/Icon/Icon';

import * as downlaodFlashMessages from './DownloadFlashMessages';
import { buildErrorLocation, processExport } from './productGroupDownloadHelpers';
import { ThunkDispatch } from '~/Store';

type Props = {
	exportType: ExportType;
	group: UserProductGroup;
	title: string;
};

const ProductGroupDownloadButton: React.FC<React.PropsWithChildren<Props>> = (props) => {
	// TODO: Add correct type
	const dispatch = useDispatch<ThunkDispatch<any>>();

	const handleDownloadRequest = async (type: ExportType) => {
		dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));
		dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
		dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
		dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));
		dispatch(removeFlashMessageByText(downlaodFlashMessages.RequiresAtMostOneRomanianProductForEachDatasource));

		try {
			await dispatch(commitRoMaxPriceUpdate(props.group.id));
			const response = (await dispatch(createExport(props.group.id, type))) as unknown as { export: Export };
			dispatch(processExport(response.export));
		} catch (error) {
			if (!(error instanceof RoMaxPriceErrorResponse)) {
				dispatch(handleError(error as Error));
				return;
			}

			const validationResult = error.toValidationResult();
			if (validationResult.groupId !== null) {
				dispatch(push(`/product-groups/${validationResult.groupId}`));
			}

			const errorLocation =
				dispatch(buildErrorLocation(validationResult.productErrors)) +
				formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

			if (error.handleRequiredProductSettingsMissing()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
			}
			if (error.handleRequiresMinOneReferenceProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation));
			}
			if (error.handleRequiresMaxOneReferenceProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
			}
			if (error.handleRequiresMaxOneProductPerDataSource()) {
				dispatch(
					flashErrorMessage(downlaodFlashMessages.RequiresAtMostOneRomanianProductForEachDatasource + errorLocation)
				);
			}
			if (error.handleRequiresReferenceSettingsErrorCode()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
			}
			if (error.handleCalculationRequiresAtLeastOneProductList()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
			}
			if (error.handleExportLimitExceeded()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.LimitExceededMessage + errorLocation));
			}
			if (error.handleIllegalPriceTypeSelected()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
			}
			dispatch(handleRoMaxPriceUpdateResponse(error, props.group.id));
		}
	};

	return (
		<Button styles={primaryIconButtonStyles} onClick={() => handleDownloadRequest(props.exportType)}>
			<Icon name='download'>download</Icon> {props.title}
		</Button>
	);
};

ProductGroupDownloadButton.displayName = 'ProductGroupDownloadPopover.ProductGroupDownloadButton';

export default ProductGroupDownloadButton;
